import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, LogOut, User, ChevronDown } from "lucide-react";
import { BRAND } from "@/data/site";
import { useAuth } from "@/lib/auth";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/gallery", label: "Gallery" },
  { to: "/reviews", label: "Reviews" },
  { to: "/how-it-works", label: "How it works" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMenuOpen(false); }, [location.pathname]);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const dashHref = user && user.role
    ? user.role === "customer" ? "/me/bookings"
      : user.role === "contractor" ? "/contractor"
      : "/admin"
    : "/login";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#050806]/85 backdrop-blur-md border-b border-[#1F3325]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between h-20">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3">
          <img src={BRAND.logoUrl} alt="" className="h-12 w-12 object-contain" />
          <div className="hidden sm:block leading-none">
            <div className="font-display text-[#F0F4F1] text-lg uppercase tracking-wider">Green</div>
            <div className="font-display text-[#8DE038] text-xs uppercase tracking-[0.3em]">Connect</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
              className={({ isActive }) =>
                `font-display uppercase text-sm tracking-widest transition-colors ${
                  isActive ? "text-[#8DE038]" : "text-[#F0F4F1] hover:text-[#8DE038]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user && user.id ? (
            <div className="relative">
              <button
                data-testid="user-menu-toggle"
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex items-center gap-2 px-3 py-2 border border-[#1F3325] hover:border-[#8DE038]"
              >
                <User size={16} className="text-[#8DE038]"/>
                <span className="font-display uppercase text-xs tracking-widest text-[#F0F4F1] max-w-[140px] truncate">{user.name}</span>
                <ChevronDown size={14} className="text-[#9CAEA3]"/>
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-[#0F1712] border border-[#1F3325] py-1">
                  <Link to={dashHref} className="block px-4 py-2 font-display uppercase tracking-widest text-xs text-[#F0F4F1] hover:bg-[#1F3325]">
                    {user.role === "customer" ? "My Bookings" : user.role === "contractor" ? "Contractor Portal" : "Admin Dashboard"}
                  </Link>
                  <button onClick={doLogout} data-testid="logout-btn" className="w-full text-left flex items-center gap-2 px-4 py-2 font-display uppercase tracking-widest text-xs text-[#F0F4F1] hover:bg-[#1F3325]">
                    <LogOut size={14}/> Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-btn" className="btn-secondary text-xs">Sign in</Link>
              <Link to="/book" data-testid="header-book-btn" className="btn-primary text-xs">Book Now</Link>
            </>
          )}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-[#F0F4F1] p-2"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#050806] border-t border-[#1F3325]">
          <div className="px-5 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className={({ isActive }) =>
                `font-display uppercase text-base tracking-widest ${isActive ? "text-[#8DE038]" : "text-[#F0F4F1]"}`
              }>{n.label}</NavLink>
            ))}
            {user && user.id ? (
              <>
                <Link to={dashHref} className="font-display uppercase text-base tracking-widest text-[#8DE038]">
                  {user.role === "customer" ? "My Bookings" : user.role === "contractor" ? "Contractor Portal" : "Admin"}
                </Link>
                <button onClick={doLogout} className="text-left font-display uppercase text-base tracking-widest text-[#F0F4F1]">Logout</button>
              </>
            ) : (
              <div className="flex gap-3 pt-3 border-t border-[#1F3325]">
                <Link to="/login" className="btn-secondary text-xs flex-1 justify-center">Sign in</Link>
                <Link to="/book" className="btn-primary text-xs flex-1 justify-center">Book</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
